import { InputAdornment, IconButton } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import React, { ReactNode, memo, useEffect, useMemo, useState } from "react";
import { OutlinedTextFieldProps, StandardTextFieldProps } from "@mui/material";

import TextField from "../TextField";
import TitleValue from "../TitleValue";
import Typography from "../Typography";
import Helpers from "../../commons/helpers";

import { Mode } from "../../constants/enums";
import InputMask from "react-input-mask";

interface IProps extends Omit<OutlinedTextFieldProps | StandardTextFieldProps, "variant"> {
    mode?: number;
    maxLength?: number;

    secure?: boolean;
    isMoney?: boolean;
    readOnly?: boolean;
    isFullRow?: boolean;
    hideBorder?: boolean;
    hideErrorMessage?: boolean;

    type?: string;
    label?: string;
    unit?: ReactNode;
    placeholder?: string;
    errorMessage?: string;
    decimalFormat?: string; // example (8, 2)
    variant?: "standard" | "outlined";
    step?: string;

    onBlur?: (value: any) => void;
    onChangeValue?: (value: any) => void;
    // [key: string]: any;
}

const FormField: React.FC<IProps> = ({
    mode,
    type,
    unit,
    label,
    secure,
    isMoney,
    variant,
    readOnly,
    maxLength,
    isFullRow,
    hideBorder,
    placeholder,
    errorMessage,
    decimalFormat,
    hideErrorMessage,
    step,
    onBlur,
    onChangeValue,
    ...rest
}: IProps) => {
    const [showPassword, setShowPassword] = React.useState<boolean>(false);
    const onShowPassword = () => {
        setShowPassword(!showPassword);
    };

    useEffect(() => {
        if (isMoney) {
            setMaskFormat({
                mask: getMaskFormat((rest.value + "" || "").replace(/\d(?=(\d{3})+$)/g, `$&,`)),
                value: (rest.value + "" || "").replace(/\d(?=(\d{3})+$)/g, `$&,`),
            });
        }
    }, [rest.value]);

    const onMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const defaultFormat = `9\x00999\x00999\x00999\x00999`;

    const getMaskFormat = (value: string) => {
        let mask: any = "";

        if (!Helpers.isNullOrEmpty(decimalFormat)) {
            const num = Number(decimalFormat.split(",")[0]);
            const decimals = Number(decimalFormat.split(",")[1]);
            for (let i = 0; i < num; i++) {
                mask += "9";
            }

            if (decimals > 0) {
                mask += ".";
            }

            for (let j = 0; j < decimals; j++) {
                mask += "9";
            }

            return mask;
        }

        const l = value.replaceAll(`\x00`, "").replaceAll(`,`, "").length;

        if (l < 4) {
            return defaultFormat;
        }

        for (let i = 0; i < l; i++) {
            mask += "9";
        }

        mask = mask.replace(/\d(?=(\d{3})+$)/g, `$&,`);
        while (mask.length <= defaultFormat.length) {
            mask += "\x00999";
        }

        return mask;
    };

    const [maskFormat, setMaskFormat] = useState<any>({
        mask: getMaskFormat((rest.value + "" || "").replace(/\d(?=(\d{3})+$)/g, `$&,`)),
        value: (rest.value + "" || "").replace(/\d(?=(\d{3})+$)/g, `$&,`),
    });

    const convertToOrginValue = (value: any) => {
        return value.replaceAll(`\x00`, "").replaceAll(`,`, "");
    };

    const beforeMaskedStateChange = ({ nextState }: any) => {
        let newValue: string = nextState.value;
        newValue = newValue.replace(/\,$/, "");
        return {
            ...nextState,
            value: newValue,
        };
    };

    const handleOnInput = (e: any) => {
        e.target.value = Helpers.checkMaxLengthTypeNumber(e.target.value, maxLength);
    };

    const newValue = useMemo(() => {
        const value = rest.value ? Number(rest.value) : Number(rest.defaultValue);
        if (isMoney) return Helpers.formatCurrency(value);
        return rest.value || rest.defaultValue || "";
    }, [rest.value, rest.defaultValue, isMoney]);

    if (mode === Mode.View) {
        return <TitleValue title={label || ""} isFullRow={isFullRow} value={(newValue) + ` ${unit ? unit : ""}`} />;
    }

    if (secure) {
        return (
            <TextField
                {...rest}
                fullWidth
                key={rest.defaultValue || label}
                label={label}
                variant={variant}
                hideBorder={hideBorder}
                placeholder={placeholder}
                InputLabelProps={{ shrink: true }}
                type={showPassword ? "text" : "password"}
                error={!Helpers.isNullOrEmpty(errorMessage)}
                helperText={hideErrorMessage ? "" : errorMessage}
                inputProps={{ maxLength: maxLength, readOnly: readOnly }}
                onBlur={(event: any) => Helpers.isFunction(onBlur) && onBlur(event.target.value)}
                onChange={(event: any) => Helpers.isFunction(onChangeValue) && onChangeValue(event.target.value)}
                //check maxlenth type number
                onInput={Helpers.isNullOrEmpty(maxLength) ? undefined : handleOnInput}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton aria-label="toggle password visibility" onClick={onShowPassword} onMouseDown={onMouseDown}>
                                {showPassword ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />
        );
    } else if (isMoney || decimalFormat) {
        if (rest.disabled) {
            return (
                <TextField
                    {...rest}
                    fullWidth
                    key={rest.defaultValue || label}
                    type={type}
                    label={label}
                    variant={variant}
                    hideBorder={hideBorder}
                    placeholder={placeholder}
                    InputLabelProps={{ shrink: true }}
                    error={!Helpers.isNullOrEmpty(errorMessage)}
                    helperText={hideErrorMessage ? "" : errorMessage}
                    inputProps={{ maxLength: maxLength, readOnly: readOnly }}
                    value={isMoney ? Helpers.formatCurrency(rest.value as any) : rest.value}
                    onBlur={(event: any) => Helpers.isFunction(onBlur) && onBlur(event.target.value)}
                    onChange={(event: any) => Helpers.isFunction(onChangeValue) && onChangeValue(event.target.value)}
                    //check maxlenth type number
                    onInput={Helpers.isNullOrEmpty(maxLength) ? undefined : handleOnInput}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <Typography variant="button">{unit && unit}</Typography>
                            </InputAdornment>
                        ),
                    }}
                />
            );
        }
        return (
            <InputMask
                {...maskFormat}
                disabled={false}
                maskPlaceholder={null}
                onChange={(event: any) => {
                    const inputValue = event.target.value;
                    setMaskFormat({
                        mask: getMaskFormat(inputValue),
                        value: inputValue,
                    });
                    Helpers.isFunction(onChangeValue) && onChangeValue(convertToOrginValue(inputValue));
                }}
                onBlur={(event: any) => {
                    const inputValue = event.target.value;
                    setMaskFormat({
                        mask: getMaskFormat(inputValue),
                        value: inputValue,
                    });
                    Helpers.isFunction(onBlur) && onBlur(convertToOrginValue(inputValue));
                }}
                beforeMaskedStateChange={beforeMaskedStateChange}
            >
                <TextField
                    fullWidth
                    key={rest.defaultValue || label}
                    label={label}
                    variant={variant}
                    hideBorder={hideBorder}
                    required={rest.required}
                    placeholder={placeholder}
                    InputLabelProps={{ shrink: true }}
                    error={!Helpers.isNullOrEmpty(errorMessage)}
                    helperText={hideErrorMessage ? "" : errorMessage}
                    inputProps={{ maxLength: maxLength, readOnly: readOnly }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <Typography variant="button">{unit && unit}</Typography>
                            </InputAdornment>
                        ),
                    }}
                />
            </InputMask>
        );
    } else {
        return (
            <TextField
                fullWidth
                key={rest.defaultValue || label}
                type={type}
                label={label}
                variant={variant}
                hideBorder={hideBorder}
                placeholder={placeholder}
                InputLabelProps={{ shrink: true }}
                error={!Helpers.isNullOrEmpty(errorMessage)}
                helperText={hideErrorMessage ? "" : errorMessage}
                inputProps={step ? { maxLength, readOnly, step } : { maxLength, readOnly }}
                onBlur={(event: any) => Helpers.isFunction(onBlur) && onBlur(event.target.value)}
                onChange={(event: any) => Helpers.isFunction(onChangeValue) && onChangeValue(event.target.value)}
                //check maxlenth type number
                onInput={Helpers.isNullOrEmpty(maxLength) ? undefined : handleOnInput}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <Typography variant="button">{unit && unit}</Typography>
                        </InputAdornment>
                    ),
                }}
                {...rest}
            />
        );
    }
};

// Declaring default props for FormField
FormField.defaultProps = {
    label: "",
};

export default memo(FormField, (prev, next) => {
    if (
        prev.mode !== next.mode ||
        prev.value !== next.value ||
        prev.required !== next.required ||
        prev.disabled !== next.disabled ||
        prev.defaultValue !== next.defaultValue ||
        prev.label !== next.label ||
        prev.placeholder !== next.placeholder ||
        prev.InputProps !== next.InputProps ||
        prev.inputProps !== next.inputProps ||
        prev.type !== next.type ||
        prev.unit !== next.unit ||
        prev.secure !== next.secure ||
        prev.isMoney !== next.isMoney ||
        prev.variant !== next.variant ||
        prev.readOnly !== next.readOnly ||
        prev.maxLength !== next.maxLength ||
        prev.isFullRow !== next.isFullRow ||
        prev.hideBorder !== next.hideBorder ||
        prev.errorMessage !== next.errorMessage ||
        prev.decimalFormat !== next.decimalFormat ||
        prev.onBlur !== next.onBlur ||
        prev.onChangeValue !== next.onChangeValue ||
        prev.hideErrorMessage !== next.hideErrorMessage
    )
        return false;
    return true;
});
